import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './EmailReportAnalytics.css'; // Using the new CSS file

const API_BASE = process.env.REACT_APP_API_URL;

// The onNavigate prop is expected from a parent component to handle view switching.
const EmailLogs = ({ onNavigate }) => {
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ page: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // State to hold the current filter values
    const [filters, setFilters] = useState({
        recipientEmail: '',
        subject: '',
        templateAction: '',
        status: '',
        userId: ''
    });

    // State to hold the active filters that trigger the API call
    const [activeFilters, setActiveFilters] = useState({});

    // Use useCallback to memoize the fetch function
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Build query parameters from pagination and active filters
            const params = new URLSearchParams({
                page: pagination.page,
                limit: 15,
                ...activeFilters
            });

            const response = await axios.get(`${API_BASE}/api/emails?${params.toString()}`);
            
            const { docs, totalPages, hasNextPage, hasPrevPage, page: currentPage } = response.data;
            
            setLogs(docs);
            setPagination({ totalPages, hasNextPage, hasPrevPage, page: currentPage });

        } catch (err) {
            setError('Failed to fetch email logs. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, activeFilters]); // Dependency array includes activeFilters

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]); // useEffect depends on the memoized fetchLogs function

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        setPagination(p => ({ ...p, page: 1 })); // Reset to first page on new filter
        setActiveFilters(filters);
    };

    const handleClearFilters = () => {
        setFilters({ recipientEmail: '', subject: '', templateAction: '', status: '', userId: '' });
        setActiveFilters({});
        setPagination(p => ({ ...p, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.totalPages) {
            setPagination(p => ({ ...p, page: newPage }));
        }
    };

    if (loading) return <div className="emaillogs-message-center">Loading email logs...</div>;
    if (error) return <div className="emaillogs-message-center emaillogs-error">{error}</div>;

    return (
        <div className="emaillogs-container">
            {/* <div className="emaillogs-header">
                <button className="emaillogs-nav-button" onClick={() => onNavigate('templates')}>
                    Email Templates
                </button>
            </div> */}

            {/* --- Filter Section --- */}
            <div className="emaillogs-filters">
                <input type="text" name="recipientEmail" placeholder="Filter by Recipient" value={filters.recipientEmail} onChange={handleFilterChange} />
                <input type="text" name="subject" placeholder="Filter by Subject" value={filters.subject} onChange={handleFilterChange} />
                <input type="text" name="templateAction" placeholder="Filter by Action" value={filters.templateAction} onChange={handleFilterChange} />
                <input type="text" name="userId" placeholder="Filter by User ID" value={filters.userId} onChange={handleFilterChange} />
                <select name="status" value={filters.status} onChange={handleFilterChange}>
                    <option value="">All Statuses</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                </select>
                <button onClick={handleApplyFilters}>Apply Filters</button>
                <button onClick={handleClearFilters}>Clear</button>
            </div>

            <div className="emaillogs-table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>Recipient</th>
                            <th>Subject</th>
                            <th>Action</th>
                            <th>Status</th>
                            <th>Sent At</th>
                            <th>User ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.length > 0 ? (
                            logs.map((log) => (
                                <tr key={log._id}>
                                    <td>{log.recipientEmail}</td>
                                    <td>{log.subject || 'N/A'}</td>
                                    <td>{log.templateAction}</td>
                                    <td>
                                        <span className={`emaillogs-status-badge emaillogs-status-${log.status}`}>
                                            {log.status}
                                        </span>
                                    </td>
                                    <td>{new Date(log.sentAt).toLocaleString()}</td>
                                    <td>{log.userId || 'System'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6">No logs found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="emaillogs-pagination">
                <span>
                    Page {pagination.page} of {pagination.totalPages}
                </span>
                <button onClick={() => handlePageChange(pagination.page - 1)} disabled={!pagination.hasPrevPage}>
                    Previous
                </button>
                <button onClick={() => handlePageChange(pagination.page + 1)} disabled={!pagination.hasNextPage}>
                    Next
                </button>
            </div>
        </div>
    );
};

export default EmailLogs;